import {
  levelOptionTitles,
  levelOptions,
} from 'algolia-api/search/sidefilters';
import { SearchFacet } from 'algolia-api/types/SearchFacet';
import { ACTION_TYPE_PLURAL } from 'utils/constants/general/actionTypePlural';
import { AREAS_OF_FOCUS } from 'utils/constants/general/areasOfFocus';
import { AREAS_OF_STUDY } from 'utils/constants/general/areasOfStudy';
import { EDUCATION_LEVELS } from 'utils/constants/general/educationLevels';
import { INTERNSHIP_FUNCTIONS } from 'utils/constants/general/internshipFunctions';
import { JOB_FUNCTIONS } from 'utils/constants/general/jobFunctions';
import { LOCALES } from 'utils/constants/general/locales';
import { ORG_TYPES } from 'utils/constants/general/orgTypes';
import { ORG_TYPES_ALLOW_TO_POST_VOLOPS } from 'utils/constants/general/orgTypesAllowToPostVolops';
import { PAYMENT_LEVELS } from 'utils/constants/general/paymentLevels';
import { PROFESSIONAL_LEVELS } from 'utils/constants/general/professionalLevels';
import { RECENCY_PERIODS } from 'utils/constants/general/recency';
import { VOLOP_FUNCTIONS } from 'utils/constants/general/volopFunctions';
import { VOLOP_SOURCE_FOR_ACTION_TYPE } from 'utils/constants/general/volopSourceForActionType';
import { VOLOP_SOURCES } from 'utils/constants/general/volopSources';
import { difference, removeKeys } from 'utils/functional';

const orgTypeFacet: SearchFacet = {
  id: 'orgTypeFacet',
  name: 'orgType',
  title: getText('Org Type'),
  type: 'array',
  options: levelOptions(ORG_TYPES),
  optionTitles: levelOptionTitles(ORG_TYPES),
  sort: false,
};

export const MAIN_SEARCH_FACETS: Record<string, SearchFacet> = {
  locationTypeFacet: {
    id: 'locationTypeFacet',
    name: 'locationType',
    title: getText('Location'),
    type: 'array',
    options: ['ONSITE', 'REMOTE', 'HYBRID', 'REMOTE_TEMPORARY'],
    optionTitles: {
      ONSITE: getText('On-Site'),
      REMOTE: getText('Remote'),
      HYBRID: getText('Hybrid'),
      REMOTE_TEMPORARY: getText('Temporarily Remote'),
    },
  },
  employmentTypeFacet: {
    id: 'employmentTypeFacet',
    name: 'jobType',
    title: getText('Job Type'),
    type: 'array',
    options: ['FULL_TIME', 'PART_TIME', 'TEMPORARY', 'CONTRACT'],
    optionTitles: {
      FULL_TIME: getText('Full Time'),
      PART_TIME: getText('Part Time'),
      TEMPORARY: getText('Temporary'),
      CONTRACT: getText('Contract'),
    },
  },
  hasLocationFacet: {
    id: 'hasLocationFacet',
    name: 'hasLocation',
    title: getText('Has Location'),
    type: 'boolean',
    options: ['YES', 'NO'],
    optionTitles: {
      YES: getText('Has Location'),
      NO: getText('No Location'),
    },
  },
  paymentFacet: {
    id: 'paymentFacet',
    name: 'payment',
    title: getText('Compensation'),
    type: 'array',
    options: levelOptions(PAYMENT_LEVELS),
    optionTitles: levelOptionTitles(PAYMENT_LEVELS),
  },
  educationFacet: {
    id: 'educationFacet',
    name: 'education',
    title: getText('Education Level'),
    type: 'array',
    options: levelOptions(EDUCATION_LEVELS),
    optionTitles: levelOptionTitles(EDUCATION_LEVELS),
  },
  actionTypeFacet: {
    id: 'actionTypeFacet',
    name: 'actionType',
    title: getText('Opportunity Type'),
    type: 'array',
    options: difference(levelOptions(ACTION_TYPE_PLURAL), ['NONE', 'OTHER']),
    optionTitles: removeKeys<string, string>(
      levelOptionTitles(ACTION_TYPE_PLURAL),
      ['NONE', 'OTHER'],
    ),
    facetToResetOnChange: 'volopSourceFacet',
    includeAdditionalOption: {
      VOLOP: 'OTHER',
    },
  },
  professionalLevelFacet: {
    id: 'professionalLevelFacet',
    name: 'professionalLevel',
    title: getText('Experience Level'),
    type: 'array',
    options: levelOptions(PROFESSIONAL_LEVELS),
    optionTitles: levelOptionTitles(PROFESSIONAL_LEVELS),
  },
  localeFacet: {
    id: 'localeFacet',
    name: 'locale',
    title: getText('Listing Language'),
    type: 'array',
    options: levelOptions(LOCALES),
    optionTitles: levelOptionTitles(LOCALES),
  },
  actionDateFacet: {
    id: 'actionDateFacet',
    name: 'actionDate',
    names: ['endsGT', 'startsLT'],
    title: getText('Date'),
    type: 'date',
    // Do not apply to date filters
    options: [],
    optionTitles: {},
  },
  orgTypeFacet,
  volopOrgTypeFacet: {
    ...orgTypeFacet,
    id: 'volopOrgTypeFacet',
    options: levelOptions(ORG_TYPES_ALLOW_TO_POST_VOLOPS),
    optionTitles: levelOptionTitles(ORG_TYPES_ALLOW_TO_POST_VOLOPS),
  },
  jobKeywordsFacet: {
    id: 'jobKeywordsFacet',
    name: 'functions',
    title: getText('Job Function'),
    type: 'array',
    options: Object.keys(JOB_FUNCTIONS),
    optionTitles: JOB_FUNCTIONS,
  },
  internshipKeywordsFacet: {
    id: 'internshipKeywordsFacet',
    name: 'functions',
    title: getText('Functions'),
    type: 'array',
    options: Object.keys(INTERNSHIP_FUNCTIONS),
    optionTitles: INTERNSHIP_FUNCTIONS,
  },
  volopKeywordsFacet: {
    id: 'volopKeywordsFacet',
    name: 'functions',
    title: getText('Skills'),
    type: 'array',
    options: Object.keys(VOLOP_FUNCTIONS),
    optionTitles: VOLOP_FUNCTIONS,
  },
  orgAreasOfFocusFacet: {
    id: 'orgAreasOfFocusFacet',
    name: 'areasOfFocus',
    title: getText('Issue Areas'),
    type: 'array',
    options: Object.keys(AREAS_OF_FOCUS),
    optionTitles: AREAS_OF_FOCUS,
  },
  volopAreasOfFocusFacet: {
    id: 'volopAreasOfFocusFacet',
    name: 'areasOfFocus',
    title: getText('Issue Areas'),
    type: 'array',
    options: Object.keys(AREAS_OF_FOCUS),
    optionTitles: AREAS_OF_FOCUS,
  },
  volopSourceFacet: {
    id: 'volopSourceFacet',
    name: 'source',
    title: getText('Source'),
    type: 'array',
    options: Object.keys(VOLOP_SOURCES),
    optionTitles: VOLOP_SOURCES,
    optionCullFn: (filters) => {
      const actionTypeFacet = filters.actionType ||
        filters.actionTypeFacet || ['VOLOP'];
      const allFiltersSelected =
        actionTypeFacet instanceof Array &&
        actionTypeFacet.length ===
          MAIN_SEARCH_FACETS.actionTypeFacet.options.length;

      if (!allFiltersSelected) {
        const sources = new Set<string>();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (actionTypeFacet as string[]).forEach((facet: any) => {
          // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          VOLOP_SOURCE_FOR_ACTION_TYPE[facet].forEach((source: any) => {
            sources.add(source);
          });
        });
        return Array.from(sources).filter((source) => source !== 'NONE');
      }

      return Object.keys(VOLOP_SOURCES);
    },
  },
  publicPrivateFacet: {
    id: 'publicPrivateFacet',
    name: 'isPublicSchool',
    title: getText('Public / Private'),
    type: 'boolean',
    options: ['YES', 'NO'],
    optionTitles: {
      YES: getText('Public'),
      NO: getText('Private'),
    },
  },
  gradAreasOfStudyFacet: {
    id: 'gradAreasOfStudyFacet',
    name: 'areasOfStudy',
    title: getText('Areas of Study'),
    type: 'array',
    options: Object.keys(AREAS_OF_STUDY),
    optionTitles: AREAS_OF_STUDY,
  },
  jobFamilyFacet: {
    id: 'jobFamilyFacet',
    name: 'synonymId',
    filterKey: 'compdb.familyId',
    title: getText('Similar Jobs'),
    type: 'string',
    options: [],
    optionTitles: {},
    lazy: true,
    hideInSideFilter: true,
  },
  recencyFacet: {
    id: 'recencyFacet',
    name: 'recency',
    title: getText('Date Posted'),
    type: 'string',
    options: levelOptions(RECENCY_PERIODS),
    optionTitles: levelOptionTitles(RECENCY_PERIODS),
  },
};
